<template>
  <div class="user-manual-select" :class="{ mobile: isMobile }">
    <div v-if="selectedId === ''" class="select-mode">
      <div class="select-header">
        <h2 class="select-title">
          {{ $t("selectManual") || "Select Manual" }}
        </h2>
        <p class="select-subtitle">
          {{ $t("selectManualDesc") || "Choose a manual to view" }}
        </p>
      </div>
      <div class="manual-options">
        <div
          v-for="opt in selectOptions"
          :key="opt.id"
          @click="selectManual(opt.id)"
          class="manual-option"
        >
          <div class="option-content">
            <div class="option-icon">📖</div>
            <div class="option-text">
              <h3 class="option-title">{{ opt.name }}</h3>
              <p class="option-desc">
                {{ $t("clickToView") || "Click to view manual" }}
              </p>
            </div>
            <div class="option-arrow">→</div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="selected-mode">
      <div class="pdf-container">
        <vue-pdf-app :pdf="pdf.src" pageScale="page-fit" :config="pdfConfig" />
      </div>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import { mapState } from "vuex";
import VuePdfApp from "vue-pdf-app";
export default Vue.extend({
  name: "UserManualSelect",
  components: { VuePdfApp },

  data() {
    return {
      selectedId: ""
    };
  },

  computed: {
    ...mapState("app", {
      isMobile: "isMobile"
    }),
    ...mapState("assets", {
      oss: "oss"
    }),

    routeId() {
      return this.$route.params.id;
    },

    selectOptions() {
      if (this.routeId === "pet-brick") {
        return [
          { id: "pet-brick", name: "PETBRICK 65 User Manual" },
          { id: "mecha-65", name: "MECHA-65 User Manual" }
        ];
      }
      return [];
    },

    pdf() {
      const pdfMap = {
        "pet-brick": {
          src: this.oss.pdf_pet_brick,
          name: "PETBRICK 65 User Manual"
        },
        "mecha-65": {
          src: this.oss.pdf_mecha_65,
          name: "MECHA 65 User Manual"
        }
      };
      return pdfMap[this.selectedId] || { src: "", name: "" };
    },

    currentManualName() {
      const selected = this.selectOptions.find(
        opt => opt.id === this.selectedId
      );
      return selected ? selected.name : "";
    },

    pdfConfig() {
      return {
        toolbar: {
          toolbarViewerLeft: {
            findbar: false
          },
          toolbarViewerMiddle: {
            scaleSelectContainer: false
          },
          toolbarViewerRight: false
        },
        secondaryToolbar: false
      };
    }
  },

  watch: {
    routeId() {
      this.selectedId = "";
      this.updateDocumentTitle();
    },

    selectedId() {
      this.updateDocumentTitle();
    }
  },

  mounted() {
    this.updateDocumentTitle();
  },

  methods: {
    selectManual(id) {
      this.selectedId = id;
    },

    goBack() {
      this.selectedId = "";
    },

    updateDocumentTitle() {
      if (this.selectedId === "") {
        document.title = this.$t("selectManual") || "Select Manual";
      } else {
        const selected = this.selectOptions.find(
          opt => opt.id === this.selectedId
        );
        document.title = selected ? selected.name : "User Manual";
      }
    }
  }
});
</script>

<style scoped lang="scss">
.user-manual-select {
  width: 100%;
  background: #f8f9fa;
  &.mobile {
    .select-mode {
      padding: 16px;
    }

    .manual-options {
      gap: 16px;
    }

    .manual-option {
      padding: 28px;
    }

    .option-title {
      font-size: 22px;
      font-weight: 700;
    }

    .option-desc {
      font-size: 18px;
    }

    .back-btn {
      padding: 10px 16px;
      font-size: 14px;
    }

    .pdf-header {
      height: 50px;
      padding: 0 12px;
    }

    .pdf-container {
      height: 100%;
    }
  }
}

.select-mode {
  padding: 32px;
  max-width: 800px;
  margin: 0 auto;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.select-header {
  text-align: center;
  margin-bottom: 40px;
}

.select-title {
  font-size: 32px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 12px 0;
}

.select-subtitle {
  font-size: 16px;
  color: #6c757d;
  margin: 0;
}

.manual-options {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.manual-option {
  background: white;
  border-radius: 12px;
  padding: 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 2px solid transparent;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    border-color: #007bff;
  }

  &:active {
    transform: translateY(0);
  }
}

.option-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.option-icon {
  font-size: 32px;
  flex-shrink: 0;
}

.option-text {
  flex: 1;
}

.option-title {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 4px 0;
}

.option-desc {
  font-size: 14px;
  color: #6c757d;
  margin: 0;
}

.option-arrow {
  font-size: 20px;
  color: #007bff;
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.manual-option:hover .option-arrow {
  transform: translateX(4px);
}

.selected-mode {
  width: 100%;
  height: 100vh;
  background: white;
  display: flex;
  flex-direction: column;
}

.pdf-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  height: 60px;
  flex-shrink: 0;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s ease;

  &:hover {
    background: #5a6268;
  }
}

.back-icon {
  font-size: 16px;
}

.back-text {
  white-space: nowrap;
}

.pdf-container {
  flex: 1;
  height: 100%;
}

.pdf-viewer {
  width: 100%;
  height: 100%;
}

// 响应式断点
@media (max-width: 768px) {
  .select-title {
    font-size: 36px;
    font-weight: 700;
  }

  .select-subtitle {
    font-size: 20px;
  }

  .option-content {
    gap: 12px;
  }

  .option-icon {
    font-size: 28px;
  }

  .back-text {
    display: none;
  }
}

@media (max-width: 480px) {
  .select-mode {
    padding: 28px 24px;
  }

  .select-title {
    font-size: 32px;
    font-weight: 700;
  }

  .manual-option {
    padding: 28px;
    border-radius: 16px;
  }

  .option-title {
    font-size: 22px;
    font-weight: 700;
  }

  .option-desc {
    font-size: 18px;
  }

  .option-icon {
    font-size: 32px;
  }
}
</style>
